// The star of a block: it saves the block to the block library, and it shows the library state
// of the block. It sits in the header of both Add Block dialogs and of the Block dialog.
//
// The dialog tells the star what a click on an empty star does (`prepare`), because the three
// dialogs have different rules. The star only calls the library API and refreshes the data.
// A save or a remove waits for the reload of the library (and of the plan, in the Block dialog),
// so the star never shows the old state after the popover closes.
import { useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { Button, Input, Modal, Popover, Tooltip } from "antd";
import type { InputRef } from "antd";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import {
	BLOCK_LIBRARY_TITLE_MAX_LENGTH,
	getBlockEntryCreator,
	getRtpErrorMessage,
	suggestBlockTitle,
} from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useRtpMetricOptions } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import type { RtpBlockContent, RtpBlockTemplate } from "./types";

// What a click on an empty star does:
// - `content` (and `blockId` for a block that exists): the Title popover opens.
// - `notice`: a popover shows the text.
// - null: nothing. The dialog has shown its own error.
export type BlockStarPlan = { content: RtpBlockContent; blockId?: number } | { notice: string } | null;

type StarPop =
	| { kind: "save"; content: RtpBlockContent; blockId?: number }
	| { kind: "notice"; text: string }
	| { kind: "info"; entry: RtpBlockTemplate };

type StarState = "on" | "off";

interface BlockLibraryStarProps {
	library: RtpBlockLibraryState;
	// The id of the library entry that the content of the dialog is. Null shows an empty star.
	templateId: string | null;
	// The user cannot change the library from here. The star shows only an entry that exists.
	// A read-only star with no entry does not render.
	readOnly: boolean;
	// The tooltip of an empty star that the user can click. The default is "Save to library".
	emptyTip?: string;
	prepare: () => Promise<BlockStarPlan> | BlockStarPlan;
	// The Block dialog gives the plan reload: a save or a remove changes the block of the plan.
	refetchPlan?: () => Promise<void> | void;
	// An Add Block dialog keeps the entry id, so ADD_BLOCK links the new block to the entry. The
	// dialog still sends the id of an entry that the user edited (the star is empty then).
	onLinkChange?: (templateId: string | null) => void;
}

interface SaveBodyProps {
	inputRef: RefObject<InputRef | null>;
	title: string;
	error: string;
	saving: boolean;
	onTitleChange: (title: string) => void;
	onSave: () => void;
	onCancel: () => void;
}

const SaveBody = ({ inputRef, title, error, saving, onTitleChange, onSave, onCancel }: SaveBodyProps) => (
	<>
		<div className="rtp-star-pop-title">Save to block library</div>
		<div className="rtp-star-pop-label">Title</div>
		<Input
			ref={inputRef}
			value={title}
			maxLength={BLOCK_LIBRARY_TITLE_MAX_LENGTH}
			status={error ? "error" : undefined}
			placeholder="Title"
			onChange={(e) => onTitleChange(e.target.value)}
			onPressEnter={onSave}
		/>
		{error ? <div className="rtp-star-pop-error">{error}</div> : null}
		<div className="rtp-star-pop-foot">
			<Button size="small" data-attr="settings.rtp-block-library.star-cancel" onClick={onCancel}>
				Cancel
			</Button>
			<Button
				type="primary"
				size="small"
				className="rtp-wz-primary-btn"
				loading={saving}
				data-attr="settings.rtp-block-library.star-save"
				onClick={onSave}
			>
				Save
			</Button>
		</div>
	</>
);

interface InfoBodyProps {
	entry: RtpBlockTemplate;
	canRemove: boolean;
	onRemove: () => void;
}

const InfoBody = ({ entry, canRemove, onRemove }: InfoBodyProps) => (
	<>
		<div className="rtp-star-pop-title">In the block library</div>
		<div className="rtp-star-pop-text">
			Saved as “{entry.title}” by {getBlockEntryCreator(entry)}.
		</div>
		{entry.canEdit && canRemove ? (
			<div className="rtp-star-pop-foot">
				<Button danger size="small" data-attr="settings.rtp-block-library.star-remove" onClick={onRemove}>
					Remove from library
				</Button>
			</div>
		) : null}
	</>
);

export const BlockLibraryStar = ({
	library,
	templateId,
	readOnly,
	emptyTip = "Save to library",
	prepare,
	refetchPlan,
	onLinkChange,
}: BlockLibraryStarProps) => {
	const { byKey } = useRtpMetricOptions();
	// `pop` stays after the close, so the content does not empty while the popover fades out.
	const [pop, setPop] = useState<StarPop | null>(null);
	const titleRef = useRef<InputRef>(null);
	const [open, setOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [titleError, setTitleError] = useState("");
	const [saving, setSaving] = useState(false);

	const entry = templateId ? library.templates.find((item) => item.id === templateId) : undefined;
	const show = (next: StarPop) => {
		setPop(next);
		setOpen(true);
	};
	const close = () => setOpen(false);
	const refresh = () => Promise.all([library.reload(), refetchPlan?.()]);

	const handleClick = async () => {
		// A click on a star with an open popover closes it (the popover does that itself).
		if (open) return;
		if (entry) {
			show({ kind: "info", entry });
			return;
		}
		const plan = await prepare();
		if (!plan) return;
		if ("notice" in plan) {
			show({ kind: "notice", text: plan.notice });
			return;
		}
		setTitle(suggestBlockTitle(plan.content, (key) => byKey[key]?.label ?? key));
		setTitleError("");
		show({ kind: "save", ...plan });
	};

	const save = async () => {
		if (pop?.kind !== "save" || saving) return;
		const text = title.trim();
		if (!text) {
			setTitleError("Enter a title.");
			return;
		}
		setSaving(true);
		try {
			const created: RtpBlockTemplate = await rtpApi.createRtpBlockTemplate(
				pop.blockId == null ? { title: text, content: pop.content } : { title: text, blockId: pop.blockId }
			);
			await refresh();
			onLinkChange?.(created.id);
			close();
			message.success(`Saved to the block library as “${created.title}”`);
		} catch (e) {
			// A 400 is a rule of the library (for example a used title): the user can fix it here.
			const reason = getRtpErrorMessage(e);
			if ((e as { response?: { status?: number } })?.response?.status === 400) setTitleError(reason);
			else message.error(reason);
		}
		setSaving(false);
	};

	const confirmRemove = (target: RtpBlockTemplate) => {
		close();
		Modal.confirm({
			title: "Remove from the library?",
			content: "Blocks already on a schedule keep their content.",
			okText: "Remove",
			okButtonProps: { danger: true, "data-attr": "settings.rtp-block-library.star-remove-submit" } as any,
			cancelButtonProps: { "data-attr": "settings.rtp-block-library.star-remove-cancel" } as any,
			onOk: async () => {
				if (!(await runRtpWrite(() => rtpApi.deleteRtpBlockTemplate(target.id)))) return;
				await refresh();
				onLinkChange?.(null);
				message.success("Removed from the block library.");
			},
		});
	};

	// The keys typed in the popover must not reach the dialog behind it: Esc closes only the popover.
	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Escape") close();
		e.stopPropagation();
	};

	const renderBody = () => {
		switch (pop?.kind) {
			case "save":
				return (
					<SaveBody
						inputRef={titleRef}
						title={title}
						error={titleError}
						saving={saving}
						onTitleChange={(next) => {
							setTitle(next);
							setTitleError("");
						}}
						onSave={save}
						onCancel={close}
					/>
				);
			case "info":
				return <InfoBody entry={pop.entry} canRemove={!readOnly} onRemove={() => confirmRemove(pop.entry)} />;
			case "notice":
				return <div className="rtp-star-pop-text">{pop.text}</div>;
			default:
				return null;
		}
	};

	// A read-only star with no entry can do nothing. All hooks have run, so this return is safe.
	if (readOnly && !entry) return null;

	const state: StarState = entry ? "on" : "off";
	// No star has a tooltip while its popover is open.
	const tip = {
		on: `In the block library as “${entry?.title}”`,
		off: emptyTip,
	}[state];

	return (
		<Popover
			open={open}
			trigger="click"
			placement="bottomRight"
			destroyOnHidden
			content={
				<div className="rtp-star-pop-body" onKeyDown={handleKeyDown}>
					{renderBody()}
				</div>
			}
			// Only an outside click closes it. The star click opens it, after `prepare`.
			onOpenChange={(next) => {
				if (!next) close();
			}}
			// The title gets the focus once, when the popover is open. A ref function runs on each
			// render, so it would select the text on each key.
			afterOpenChange={(visible) => {
				if (visible) titleRef.current?.focus({ preventScroll: true, cursor: "all" });
			}}
			classNames={{ root: "rtp-wz rtp-star-pop" }}
		>
			<Tooltip title={open ? null : tip}>
				<Button
					type="text"
					size="small"
					className={`rtp-star rtp-star--${state}`}
					icon={state === "on" ? <StarFilled /> : <StarOutlined />}
					aria-label={tip}
					data-attr="settings.rtp-block-library.star"
					onClick={handleClick}
				/>
			</Tooltip>
		</Popover>
	);
};
